import {LOCAL_STORAGE_KEYS} from "../utils/constants";
import {invokeApi} from "../bl_libs/invokeApi";
import {use_e2ee} from "../utils/utils";

export const CurationPromptService = (program_id, user_message) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path   : '/pdq3/program/curation/prompt',
        method : 'POST',
        headers : {
            'Authorization' : `Bearer ${token}`
        }
    };
    requestObj['postData'] = {
        program_id,
        user_message
    };

    return invokeApi(requestObj);
}

export const CurationPromptStreamService = (program_id, user_message, allowed_competencies = null) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const baseUri = process.env.REACT_APP_MYDQ_API;
    return fetch(`${baseUri}/dq_insider/dqin/learning_program/curation_prompt`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
            program_id,
            user_message,
            ...(allowed_competencies && allowed_competencies.length > 0 ? { allowed_competencies } : {})
        })
    });
};

/**
 * LearnCreator program-creation chat — one turn, one JSON reply.
 *
 * The previous engine streamed SSE with {{marker}} tokens the client had to parse back
 * into wizard state. The DQ AI chatbot answers a whole turn at once and the server sends
 * the wizard params with it, so this goes through invokeApi like every other call and
 * inherits its token refresh instead of hand-rolling the auth header.
 *
 * @returns {Promise<{ data: { reply_markdown, stage, pending_confirmation, collected_view,
 *                             done, params, card, program_code } }>}
 */
export const CurationPromptV2Service = (program_id, user_message) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path   : '/dq_insider/dqin/learning_program/curation_prompt_v2',
        method : 'POST',
        headers : {
            'Authorization' : `Bearer ${token}`
        }
    };
    requestObj['postData'] = { program_id, user_message };

    return invokeApi(requestObj);
};

export const createProgramService = () => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path   : '/pdq3/program/new',
        method : 'POST',
        headers : {
            'Authorization' : `Bearer ${token}`
        }
    };
    requestObj['postData'] = {
    };

    return invokeApi(requestObj);
}

export const fetchProgramService = (programId) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path   : '/pdq3/program/fetch',
        method : 'POST',
        headers : {
            'Authorization' : `Bearer ${token}`
        }
    };
    requestObj['postData'] = {
        'program_id': programId
    };

    return invokeApi(requestObj);
}


export const createInsiderProgramV4Service = () => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path   : '/dq_insider/dqin/learning_program/create',
        method : 'POST',
        headers : {
            'Authorization' : `Bearer ${token}`
        }
    };
    requestObj['postData'] = {
    };

    return invokeApi(requestObj);
}

export const buildLearningProgramService = (
    code,
    full_name,
    target_audience,
    dq_codes,
    time_to_complete_minutes,
    image_url,
    theme_color,
    badge_background,
    primary_color,
    description,
    more_info,
    translations,
    program_details,
    request_type
) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path   : '/dq_insider/dqin/learning_program/build',
        method : 'POST',
        headers : {
            'Authorization' : `Bearer ${token}`
        }
    };
    requestObj['postData'] = {
        code,
        full_name,
        target_audience,
        dq_codes,
        time_to_complete_minutes,
        image_url,
        theme_color,
        badge_background,
        primary_color,
        description,
        more_info,
        translations,
        program_details,
        request_type
    };

    return invokeApi(requestObj);
}

export const updateLearningProgramService = (code, payload) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path   : '/dq_insider/dqin/learning_program/update',
        method : 'POST',
        headers : {
            'Authorization' : `Bearer ${token}`
        }
    };
    requestObj['postData'] = {
        code,
        ...payload
    };

    return invokeApi(requestObj);
}

export const getLearningProgramPreviewService = ( dq_module_code ) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/preview',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    };
    requestObj['postData'] = { dq_module_code };
    return invokeApi(requestObj);
};

export const getLearningProgramPreviewStatusService = ( dq_module_code ) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/preview/status',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    };
    requestObj['postData'] = { dq_module_code };
    return invokeApi(requestObj);
};

export const getLearningProgramSlidePreviewService = ( dq_module_code, session_no ) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/slide_preview',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    };
    requestObj['postData'] = { dq_module_code, session_no };
    return invokeApi(requestObj);
};

export const getUnrestrictedLearningProgramPreviewService = ( dq_module_code ) => {
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/unrestricted/preview',
        method: 'POST',
    };
    requestObj['postData'] = { dq_module_code };
    return invokeApi(requestObj);
};

export const getUnrestrictedLearningProgramSlidePreviewService = ( dq_module_code, session_no ) => {
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/unrestricted/slide_preview',
        method: 'POST',
    };
    requestObj['postData'] = { dq_module_code, session_no };
    return invokeApi(requestObj);
};

export const updateLearningProgramSlidePreviewService = ( dq_module_code, session_no, language, slide_content ) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/slide_preview/update',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    };
    requestObj['postData'] = { dq_module_code, session_no, language, slide_content };
    return invokeApi(requestObj);
};

export const getLearningProgramStatusService = ( dq_module_code ) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/status',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    };
    requestObj['postData'] = { dq_module_code };
    return invokeApi(requestObj);
};

export const getLearningProgramDescriptionService = ( target_audience, locales, dq_codes, subject_area ) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/description',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    };
    requestObj['postData'] = { target_audience, locales, dq_codes, subject_area };
    return invokeApi(requestObj);
};

export const isLearningProgramReadyService = ( dq_module_code ) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/is_ready',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    };
    requestObj['postData'] = { dq_module_code };
    return invokeApi(requestObj);
};

export const getInsiderProgramsService = (page = 1, limit = 10, module_type) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/insider_programs',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    };
    
    requestObj['postData'] = {
        page,
        limit
    };

    return invokeApi(requestObj);
};

/**
 * Duplicate an existing learning program into a new DRAFT in this workspace.
 *
 * The source may be one of your own programs or a published gallery template —
 * either way the copy is an ordinary program you own and can edit. Nothing is
 * regenerated, so this returns as fast as the content can be copied.
 *
 * Available to anyone who can create a program (LearnCreator create access);
 * you do NOT have to be the author of the program being copied.
 *
 * On failure the API returns `data.reason` — a stable code the caller should
 * map to a specific message: program_not_ready, program_inactive,
 * program_limit_reached, insufficient_credits, duplicate_in_progress.
 *
 * See my-dq-api/src/docs/Learning Program Duplication — Technical Plan.md §5.
 *
 * @param {string} dq_module_code   code of the program to copy
 * @param {string} [full_name]      name for the copy; defaults to "Copy of {name}"
 * @param {string} [idempotency_key] guards against a double-submit creating two copies
 */
export const duplicateLearningProgramService = (dq_module_code, full_name, idempotency_key) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/duplicate',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    };
    requestObj['postData'] = {
        dq_module_code,
        ...(full_name ? { full_name } : {}),
        ...(idempotency_key ? { idempotency_key } : {}),
    };
    return invokeApi(requestObj);
};

export const getInsiderProgramsAndAssessmentsService = (dq_module_codes) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/insider_programs_assessments',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    };
    requestObj['postData'] = { dq_module_codes };
    return invokeApi(requestObj);
};

export const getInsiderProgramService = (dq_module_code) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/insider_program',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    };

    requestObj['postData'] = { dq_module_code };
    return invokeApi(requestObj);
};

export const getProgramSelectedFrameworksService = (dq_module_code) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/selected_frameworks',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    };

    requestObj['postData'] = { dq_module_code };
    return invokeApi(requestObj);
};

/**
 * `institute_code` names one school INSIDE a network — schools there share the
 * network's org_code. With it, the network makes the item available to that
 * school only (partner -> network -> school -> class). Omit it for a network or
 * a standalone school.
 */
export const linkLearningProgramService = (org_code, dq_module_codes, institute_code = null) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/client/link_learning_program',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    };

    requestObj['postData'] = {
        org_code,
        dq_module_codes,
        ...(institute_code ? { institute_code } : {}),
    };
    return invokeApi(requestObj);
};

/** Link learning programs to one class — Manage Classes. */
export const linkSectionLearningProgramService = (org_code, section_code, dq_module_codes) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi(
        {
            path: '/dq_insider/dqin/client/section/link_learning_program',
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            postData: { org_code, section_code, dq_module_codes },
        },
        use_e2ee(),
    );
};

/** What is linked to `org_code` — or, with `institute_code`, to that one school of a network. */
export const getLinkedLearningProgramService = (org_code, institute_code = null) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    const q = institute_code ? `?${new URLSearchParams({ institute_code }).toString()}` : '';
    let requestObj = {
        path: `/dq_insider/dqin/client/get_linked_learning_program/${org_code}${q}`,
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    };

    return invokeApi(requestObj);
};

export const generateLearningProgramContentService = (dq_module_code, request_type) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/generate_content',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
        }
    };

    requestObj['postData'] = {
        dq_module_code,
        request_type
    };
    return invokeApi(requestObj);
};

export const listV3Level7DQCodeService = (dq_locale) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: `/dq_insider/dqin/learning_program/v3_level7_dqcode/list/${dq_locale}`,
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };

    return invokeApi(requestObj);
};

/**
 * The chatbot's opening message, shown before a program exists.
 *
 * Best-effort by design: the page has a bundled fallback, so a miss here must never
 * alarm the educator. A plain fetch is used instead of invokeApi because invokeApi
 * raises a "Server Response" toast on every failure, and this call can fail for
 * ordinary reasons (backend restarting, an older backend without the route). Resolves
 * to null on any failure; a stale token is left for the next real call to refresh.
 */
export const getCurationGreetingService = async () => {
    try {
        const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
        const baseUri = process.env.REACT_APP_MYDQ_API;
        const res = await fetch(`${baseUri}/dq_insider/dqin/learning_program/curation_greeting`, {
            method: 'GET',
            headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!res.ok) return null;
        return await res.json();
    } catch (_) {
        return null;
    }
};

export const getActiveChatInsiderProgramService = () => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/active_chat',
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    }
    return invokeApi(requestObj);
};

export const saveChatStateInsiderService = (program_id, state_data) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/chat_state/save',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };
    requestObj['postData'] = {
        program_id,
        state_data
    };
    return invokeApi(requestObj);
};

export const loadChatStateInsiderService = (program_id) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/chat_state/load',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };
    requestObj['postData'] = {
        program_id
    };
    return invokeApi(requestObj);
};

export const clearChatStateInsiderService = (program_id) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/chat_state/clear',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };
    requestObj['postData'] = {
        program_id
    };
    return invokeApi(requestObj);
};

export const removeModuleFromProgramService = (program_id, module_id) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/remove/module',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };
    requestObj['postData'] = {
        program_id,
        module_id
    };
    return invokeApi(requestObj);
};

export const removeUnitFromModuleService = (program_id, module_id, unit_id) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/remove/unit',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };
    requestObj['postData'] = {
        program_id,
        module_id,
        unit_id
    };
    return invokeApi(requestObj);
};

export const removeSessionFromUnitService = (program_id, module_id, unit_id, session_id) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/remove/session',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };
    requestObj['postData'] = {
        program_id,
        module_id,
        unit_id,
        session_id
    };
    return invokeApi(requestObj);
};

export const listV3Level7FrameService = (country_code = null, is_eu = false, age_group, dq_codes = null) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/v3_level7_framework/list',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };

    requestObj['postData'] = {
        country_code,
        is_eu,
        age_group
    };

    if (Array.isArray(dq_codes) && dq_codes.length > 0) {
        requestObj['postData'].dq_codes = dq_codes;
    }

    return invokeApi(requestObj);
};

export const getLearningProgramPlansService = () => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/plan/list',
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };
    return invokeApi(requestObj);
};

/**
 * What saving Edit program information would cost the caller now — read-only.
 * The program's first edit is free; later edits hold the rebuild's credits.
 * @param {string} program_code
 * @param {number} [sessions]  the session count the edit would build
 * @returns {{ data: { sessions, free_edit, charged, sufficient, required_credits?, balance_credits?, dqt?, shortfall?, message? } }}
 */
export const getLearningProgramEditCostService = (program_code, sessions) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/edit_cost',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };
    requestObj['postData'] = { program_code, sessions };
    return invokeApi(requestObj);
};

export const getLearningProgramLinkStatusService = (program_code) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path: '/dq_insider/dqin/learning_program/link_status',
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`
        }
    };
    requestObj['postData'] = { program_code };
    return invokeApi(requestObj);
};

/**
 * Publish a learning program (one-way action — there is no unpublish endpoint).
 * Requires the program's content to be fully generated (see isLearningProgramReadyService).
 * On success the response echoes the program with `status: LEARNING_PROGRAM_STATUS.PUBLISHED`
 * (and `already_published: true` if it was already published).
 * On failure (400) the server returns `{ message }` explaining why
 * (not found/not owned, or content not ready yet) — surfaced automatically by invokeApi.
 */
export const publishLearningProgramService = (dq_module_code) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path   : '/dq_insider/dqin/learning_program/publish',
        method : 'POST',
        headers : {
            'Authorization' : `Bearer ${token}`
        }
    };
    requestObj['postData'] = { dq_module_code };
    return invokeApi(requestObj);
}

export const updateAssessmentVisibilityLearningProgramService = (dq_module_code, session_no, show_pre_assessment, show_post_assessment) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path   : '/dq_insider/dqin/learning_program/assessments_visibility/update',
        method : 'POST',
        headers : {
            'Authorization' : `Bearer ${token}`
        }
    };
    requestObj['postData'] = {
        dq_module_code,
        session_no,
        show_pre_assessment,
        show_post_assessment
    }
    return invokeApi(requestObj);
}

// ── Program Templates gallery (DQ-authored showcase programs, read-only) ────
// See src/docs/Template-Learning-Programs-Architecture.md (my-dq-api) §8.2.
export const getTemplateLearningProgramsService = () => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    return invokeApi({
        path   : '/dq_insider/dqin/learning_program/templates/list',
        method : 'GET',
        headers : {
            'Authorization' : `Bearer ${token}`
        }
    });
}

/*
 * Template preview counterparts of getLearningProgramPreviewService /
 * getLearningProgramSlidePreviewService below. Identical request and response
 * shapes — the difference is server-side: these run _verifyPublishedTemplate
 * first, so they will only ever serve a published template. The generic
 * endpoints resolve a bare code with no ownership check at all, which is why
 * templates (owned by another insider) must NOT be read through them.
 */
export const getTemplateProgramPreviewService = ( dq_module_code ) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path   : '/dq_insider/dqin/learning_program/templates/preview',
        method : 'POST',
        headers : {
            'Authorization' : `Bearer ${token}`
        }
    };
    requestObj['postData'] = { dq_module_code };
    return invokeApi(requestObj);
}

export const getTemplateProgramSlidePreviewService = ( dq_module_code, session_no ) => {
    const token = JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.auth_token));
    let requestObj = {
        path   : '/dq_insider/dqin/learning_program/templates/slide_preview',
        method : 'POST',
        headers : {
            'Authorization' : `Bearer ${token}`
        }
    };
    requestObj['postData'] = { dq_module_code, session_no };
    return invokeApi(requestObj);
}